import { useMemo } from 'react';

import { analyticsProps } from '@class-ui/insights';
import { Box, Divider, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { ClassContainer } from './ClassContainer';
import ClassFormErrors from './ClassFormErrors';
import ClassFormFooter from './ClassFormFooter';
import ErrorForm from './ErrorForm';
import { LoadingForm } from './LoadingForm';
import Advanced from './Sections/Advanced';
import Dates from './Sections/Dates/index';
import Details from './Sections/Details';

const StyledDivider = styled(Divider)`
    margin: 2rem 0;
`;

const Container = styled(Box)`
    max-width: 750px;
    padding: 2rem;
    margin: 0 auto;
` as typeof Box;

const Title = styled(Typography)`
    margin-bottom: 2rem;
` as typeof Typography;

type ClassFormProps = {
    onClose?: () => void;
    onSuccess?: () => void;
    admin?: boolean;
};

const ClassForm = (props: ClassFormProps) => {
    const { onClose, onSuccess, admin = false } = props;

    const { t } = useTranslation('create_edit_class');

    const { errorMessage, formActionType, getClassToEditStatus } = useContainer(ClassContainer);

    const formState = useMemo(() => {
        if (formActionType === 'EDIT') {
            if (getClassToEditStatus === 'ERROR') {
                return 'error';
            }

            if (getClassToEditStatus !== 'SUCCESS') {
                return 'loading';
            }
        }

        return 'success';
    }, [formActionType, getClassToEditStatus]);

    const title = useMemo(() => {
        if (formActionType === 'CREATE') {
            return t('create-title');
        }

        if (formActionType === 'EDIT') {
            return t('edit-title');
        }

        return '';
    }, [formActionType, t]);

    return (
        <Container {...analyticsProps('class-form', { mode: formActionType, state: formState })}>
            <Title variant="h1" {...analyticsProps('class-form-title')}>
                {title}
            </Title>

            {formState === 'error' && <ErrorForm errorMessage={errorMessage} />}

            {formState === 'loading' && <LoadingForm />}

            {formState === 'success' && (
                <form>
                    <Details admin={admin} />
                    <StyledDivider />
                    <Dates />
                    <StyledDivider />
                    <Advanced parentPage={admin ? 'admin' : 'scheduler'} />
                    <ClassFormErrors />
                    <ClassFormFooter admin={admin} onSuccess={onSuccess} onClose={onClose} />
                </form>
            )}
        </Container>
    );
};

export default ClassForm;
